import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import { Save, Loader2, AlertCircle, ArrowLeft, ChevronDown, ChevronUp, Star, Camera, Phone, Clock, DollarSign, Building, Home, MapPin, Tent, Shield, Wrench, Map } from 'lucide-react';
// Photo component imports
import PhotoUpload from './PhotoUpload';
import PhotoGallery from './PhotoGallery';

// Custom Google Maps Icon Component
const GoogleMapsIcon = ({ className = "h-5 w-5" }) => (
    <svg className={className} viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <radialGradient id="shadowGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" style={{stopColor: "rgba(0,0,0,0.3)"}} />
                <stop offset="100%" style={{stopColor: "rgba(0,0,0,0)"}} />
            </radialGradient>
            <linearGradient id="pinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor: "#EA4335"}} />
                <stop offset="50%" style={{stopColor: "#E53E3E"}} />
                <stop offset="100%" style={{stopColor: "#C53030"}} />
            </linearGradient>
            <radialGradient id="innerCircle" cx="40%" cy="30%" r="60%">
                <stop offset="0%" style={{stopColor: "#ffffff"}} />
                <stop offset="100%" style={{stopColor: "#f7fafc"}} />
            </radialGradient>
        </defs>
        <ellipse cx="50" cy="110" rx="20" ry="8" fill="url(#shadowGradient)" opacity="0.4"/>
        <path d="M50 10 C65 10 77 22 77 37 C77 52 50 85 50 85 C50 85 23 52 23 37 C23 22 35 10 50 10 Z"
              fill="url(#pinGradient)" stroke="#B91C1C" strokeWidth="1"/>
        <circle cx="50" cy="37" r="12" fill="url(#innerCircle)" stroke="#E2E8F0" strokeWidth="0.5"/>
        <ellipse cx="45" cy="25" rx="8" ry="12" fill="rgba(255,255,255,0.3)" opacity="0.6"/>
        <circle cx="50" cy="37" r="12" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="0.5"/>
    </svg>
);

const CampDetailsEditor = () => {
    const { camp_location_id, userId } = useParams(); // userId should be XenForo user_id
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [campDetails, setCampDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Form data state
    const [formData, setFormData] = useState({});

    // Photo gallery refresh state
    const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);

    // Photo uploader panel state
    const [photoUploaderExpanded, setPhotoUploaderExpanded] = useState(false);

    // Collapsible sections state
    const [locationExpanded, setLocationExpanded] = useState(true);
    const [siteDetailsExpanded, setSiteDetailsExpanded] = useState(true);
    const [accessRequirementsExpanded, setAccessRequirementsExpanded] = useState(true);
    const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
    const [contactBookingExpanded, setContactBookingExpanded] = useState(false);
    const [operationalExpanded, setOperationalExpanded] = useState(false);
    const [feesExpanded, setFeesExpanded] = useState(false);
    const [administrativeExpanded, setAdministrativeExpanded] = useState(false);


    // Map state preservation functions
    const getReturnMapState = useCallback(() => {
        const returnLat = searchParams.get('returnLat');
        const returnLng = searchParams.get('returnLng');
        const returnZoom = searchParams.get('returnZoom');
        const returnFrom = searchParams.get('returnFrom');

        if (returnLat && returnLng && returnZoom && (returnFrom === 'map' || returnFrom === 'addLocation')) {
            return {
                lat: parseFloat(returnLat),
                lng: parseFloat(returnLng),
                zoom: parseFloat(returnZoom)
            };
        }
        return null;
    }, [searchParams]);

    // Function to navigate back to map with preserved state
    const goBackToMap = useCallback(() => {
        const mapState = getReturnMapState();

        if (mapState) {
            // Navigate back to map with preserved location and zoom
            const params = new URLSearchParams();
            params.set('returnLat', mapState.lat.toString());
            params.set('returnLng', mapState.lng.toString());
            params.set('returnZoom', mapState.zoom.toString());
            params.set('returnFrom', 'map');

            navigate(`/events/camps?${params.toString()}`);
        } else {
            // Fallback to regular map navigation
            navigate('/events/camps');
        }
    }, [navigate, getReturnMapState]);

    // ENHANCED: Save and return to map with mobile icon loading fix
    const saveAndReturnToMap = useCallback(async () => {
        console.log('💾 Saving and returning to map...');
        setSaving(true);
        setError(null);

        try {
            const response = await fetch('/otgservices/editCampDetails.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    camp_location_id: camp_location_id,
                    ...formData
                })
            });

            const result = await response.json();

            if (result.success) {
                toast.success("Changes saved successfully!");
                setCampDetails(prev => ({
                    ...prev,
                    details: formData
                }));

                // MOBILE FIX: Add longer delay for mobile devices before navigation
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                const delay = isMobile ? 2000 : 1000; // 2 seconds for mobile, 1 second for desktop

                console.log(`🔄 Waiting ${delay}ms before navigation (mobile: ${isMobile})`);

                setTimeout(() => {
                    // Add force refresh parameter for mobile to ensure icons reload
                    const mapState = getReturnMapState();
                    if (mapState) {
                        const params = new URLSearchParams();
                        params.set('lat', mapState.lat.toString());
                        params.set('lng', mapState.lng.toString());
                        params.set('zoom', mapState.zoom.toString());
                        params.set('from', 'details');

                        // Add mobile refresh flag
                        if (isMobile) {
                            params.set('mobileRefresh', 'true');
                        }

                        const mapUrl = `/events/camps?${params.toString()}`;
                        console.log('🔗 Mobile-optimized navigation to:', mapUrl);
                        navigate(mapUrl);
                    } else {
                        navigate('/events/camps');
                    }
                }, delay);
            } else {
                setError(result.error?.message || 'Failed to save changes');
                setSaving(false);
            }
        } catch (err) {
            console.error('💾 Save error:', err);
            setError('Network error occurred while saving');
            setSaving(false);
        }
    }, [formData, camp_location_id, getReturnMapState, navigate]);


    const fetchCampDetails = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`/otgservices/getCampDetails.php?camp_location_id=${camp_location_id}`);
            const data = await response.json();

            if (data.success) {
                setCampDetails(data.data);
                setFormData(data.data.details || {});
            } else {
                setError(data.error?.message || 'Failed to load camp details');
            }
        } catch (err) {
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [camp_location_id]);

    useEffect(() => {
        if (camp_location_id) {
            fetchCampDetails();
        }
    }, [camp_location_id, fetchCampDetails]);



    const handlePhotoUploaderToggle = () => {
        setPhotoUploaderExpanded(!photoUploaderExpanded);
    };

    const handleLocationToggle = () => {
        setLocationExpanded(!locationExpanded);
    };

    const handleSiteDetailsToggle = () => {
        setSiteDetailsExpanded(!siteDetailsExpanded);
    };

    const handleAccessRequirementsToggle = () => {
        setAccessRequirementsExpanded(!accessRequirementsExpanded);
    };

    const handleAmenitiesToggle = () => {
        setAmenitiesExpanded(!amenitiesExpanded);
    };

    const handleContactBookingToggle = () => {
        setContactBookingExpanded(!contactBookingExpanded);
    };

    const handleOperationalToggle = () => {
        setOperationalExpanded(!operationalExpanded);
    };

    const handleFeesToggle = () => {
        setFeesExpanded(!feesExpanded);
    };

    const handleAdministrativeToggle = () => {
        setAdministrativeExpanded(!administrativeExpanded);
    };

    // Function to open Google Maps with camp coordinates
    const openGoogleMaps = () => {
        const latitude = campDetails?.basic_info?.latitude;
        const longitude = campDetails?.basic_info?.longitude;

        if (latitude && longitude) {
            const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
            window.open(mapsUrl, '_blank');
        } else {
            toast.error('No coordinates available for this campground');
        }
    };

    // Helper function to get icon for amenity
    const getAmenityIcon = (amenityName, icon) => {
        const iconMap = {
            // Utilities
            'water': '💧',
            'power': '⚡',
            'sewer': '🚰',
            'dump': '🚮',
            'wifi': '📶',
            'cell': '📱',
            'comms': '🛰️', // Starlink
            
            // Facilities
            'restroom': '🚻',
            'shower': '🚿',
            'toilet': '🚽',
            'vault': '🏠',
            'table': '🪑',
            'fire': '🔥',
            'grill': '🍖',
            'bear': '🧳',
            'trash': '🗑️',
            
            // Activities
            'hiking': '🥾',
            'fishing': '🎣',
            'swimming': '🏊',
            'boat': '🛥️',
            'bike': '🚴',
            'atv': '🏍️',
            
            // Services
            'store': '🏪',
            'wood': '🪵',
            'laundry': '🧺',
            'playground': '🛝',
            
            // Access
            'pet': '🐕',
            'wheelchair': '♿',
            'big_rig': '🚛',
            'tent': '⛺',
            'generator': '⚙️'
        };
        
        // Try to match by icon field first, then fall back to name matching
        return iconMap[icon] || iconMap[amenityName?.toLowerCase()] || '🏕️';
    };

    const handleAmenityUpdate = async (amenityId, field, value) => {
        try {
            const response = await fetch('/otgservices/updateCampAmenity.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    camp_location_id: camp_location_id,
                    amenity_id: amenityId,
                    field: field,
                    value: value
                })
            });

            const result = await response.json();

            if (result.success) {
                // Update the campDetails state to reflect the change
                setCampDetails(prev => ({
                    ...prev,
                    amenities: prev.amenities.map(amenity =>
                        amenity.id === amenityId
                            ? { ...amenity, [field]: value }
                            : amenity
                    )
                }));
                toast.success(`Amenity updated successfully!`);
            } else {
                toast.error('Failed to update amenity: ' + (result.error?.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error updating amenity:', err);
            toast.error('Network error occurred while updating amenity');
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };






    const handleSubmit = async () => {
        setSaving(true);
        setError(null);

        try {
            const response = await fetch('/otgservices/editCampDetails.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    camp_location_id: camp_location_id,
                    ...formData
                })
            });

            const result = await response.json();

            if (result.success) {
                toast.success("Changes made successfully!");
                setCampDetails(prev => ({
                    ...prev,
                    details: formData
                }));
            } else {
                setError(result.error?.message || 'Failed to save changes');
            }
        } catch (err) {
            setError('Network error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    if (!camp_location_id) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <span className="ml-2 text-red-800">No camp location ID provided in URL</span>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading camp details...</span>
            </div>
        );
    }

    if (error && !campDetails) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <span className="ml-2 text-red-800">{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <ToastContainer position="top-right" reverseOrder={false}/>
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">OFF THE GRID</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-white shadow-lg rounded-lg">
                    {/* Navigation Bar */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <button
                                    onClick={() => navigate('/events')}
                                    className="mr-2 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                                    title="Home"
                                >
                                    <Home className="h-4 w-4" />
                                </button>

                                {/* Enhanced Back to Map button with preserved state */}
                                {getReturnMapState() ? (
                                    <button
                                        onClick={goBackToMap}
                                        className="mr-2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-md"
                                        title={`Return to map at previous location (${getReturnMapState().lat.toFixed(4)}, ${getReturnMapState().lng.toFixed(4)})`}
                                    >
                                        <Map className="h-4 w-4" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => navigate('/events/camps')}
                                        className="mr-2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-md"
                                        title="Go to Camp Map"
                                    >
                                        <Map className="h-4 w-4" />
                                    </button>
                                )}

                                <button
                                    onClick={() => navigate(-1)}
                                    className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                                    title="Go Back"
                                >
                                    <ArrowLeft className="h-5 w-5" />
                                </button>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        {campDetails?.basic_info?.name}
                                    </h2>
                                    {getReturnMapState() && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            📍 Came from map location: {getReturnMapState().lat.toFixed(4)}, {getReturnMapState().lng.toFixed(4)} (zoom: {getReturnMapState().zoom.toFixed(1)})
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                {/* Description Panel */}
                <div className="mx-6 mt-4">
                    <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Description</h3>
                        <textarea
                            value={formData.description || campDetails?.basic_info?.description || ''}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md resize-vertical min-h-[100px]"
                            placeholder="Enter a description of this campground..."
                            rows="4"
                        />
                    </div>
                </div>

                {/* Photo Gallery Section */}
                <PhotoGallery
                    key={galleryRefreshKey}
                    campLocationId={camp_location_id}
                    title="Camp Photo Gallery"
                />

                {/* At a Glance - Available Amenities Icons */}
                <div className="mx-6 mt-4">
                    {campDetails?.amenities?.some(a => a.available) && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                        <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                            <span className="mr-2">👁️</span>
                            At a Glance
                        </h3>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4">
                            {campDetails.amenities
                                .filter(amenity => amenity.available)
                                .map(amenity => (
                                    <div
                                        key={amenity.id}
                                        className="flex flex-col items-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
                                        title={`${amenity.name}: ${amenity.description}`}
                                    >
                                        <div className="text-2xl mb-1 group-hover:scale-110 transition-transform">
                                            {getAmenityIcon(amenity.name, amenity.icon)}
                                        </div>
                                        <span className="text-xs text-gray-700 text-center leading-tight font-medium">
                                                {amenity.name}
                                            </span>
                                    </div>
                                ))
                            }
                        </div>
                        {campDetails.amenities.filter(a => a.available).length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                <span className="text-3xl mb-2 block">🏕️</span>
                                <p className="text-sm">No amenities marked as available yet</p>
                            </div>
                        )}
                    </div>
                )}
                </div>

                {/* Amenities - Collapsible */}
                <div className="mx-6 mt-4">
                    <div className="bg-cyan-50 rounded-lg overflow-hidden border border-cyan-100">
                    <button
                        onClick={handleAmenitiesToggle}
                        className="w-full px-4 py-4 flex items-center justify-between hover:bg-cyan-100 transition-colors bg-cyan-50"
                    >
                        <div className="flex items-center">
                            <Wrench className="h-5 w-5 mr-2 text-cyan-600" />
                            <h3 className="text-lg font-semibold text-cyan-800">Camp Amenities</h3>
                            <span className="ml-2 text-sm text-cyan-600">
                                    ({campDetails?.amenities?.filter(a => a.available).length || 0} available)
                                </span>
                        </div>
                        {amenitiesExpanded ? (
                            <ChevronUp className="h-5 w-5 text-cyan-500" />
                        ) : (
                            <ChevronDown className="h-5 w-5 text-cyan-500" />
                        )}
                    </button>

                    {amenitiesExpanded && (
                        <div className="px-4 pb-4 space-y-4">
                            {/* Utilities */}
                            {campDetails?.amenities?.filter(a => a.category === 'utilities').length > 0 && (
                                <div>
                                    <h4 className="font-medium text-cyan-800 mb-3 bg-cyan-100 px-3 py-2 rounded-md">Utilities</h4>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {campDetails.amenities
                                            .filter(amenity => amenity.category === 'utilities')
                                            .map(amenity => (
                                                <div key={amenity.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={amenity.available}
                                                                    onChange={(e) => handleAmenityUpdate(amenity.id, 'available', e.target.checked)}
                                                                    className="h-4 w-4 text-cyan-600 mr-3"
                                                                />
                                                                <div>
                                                                        <span className={`font-medium ${amenity.available ? 'text-green-700' : 'text-gray-500'}`}>
                                                                            {amenity.name}
                                                                        </span>
                                                                    <p className="text-xs text-gray-600">{amenity.description}</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            amenity.available
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                                {amenity.available ? 'Available' : 'Not Available'}
                                                            </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 text-gray-700">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={amenity.notes || ''}
                                                            onChange={(e) => {
                                                                setCampDetails(prev => ({
                                                                    ...prev,
                                                                    amenities: prev.amenities.map(a =>
                                                                        a.id === amenity.id
                                                                            ? { ...a, notes: e.target.value }
                                                                            : a
                                                                    )
                                                                }));
                                                            }}
                                                            onBlur={(e) => {
                                                                handleAmenityUpdate(amenity.id, 'notes', e.target.value);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            placeholder="Add notes about this amenity..."
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Facilities */}
                            {campDetails?.amenities?.filter(a => a.category === 'facilities').length > 0 && (
                                <div>
                                    <h4 className="font-medium text-cyan-800 mb-3 bg-cyan-100 px-3 py-2 rounded-md">Facilities</h4>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {campDetails.amenities
                                            .filter(amenity => amenity.category === 'facilities')
                                            .map(amenity => (
                                                <div key={amenity.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={amenity.available}
                                                                    onChange={(e) => handleAmenityUpdate(amenity.id, 'available', e.target.checked)}
                                                                    className="h-4 w-4 text-cyan-600 mr-3"
                                                                />
                                                                <div>
                                                                        <span className={`font-medium ${amenity.available ? 'text-green-700' : 'text-gray-500'}`}>
                                                                            {amenity.name}
                                                                        </span>
                                                                    <p className="text-xs text-gray-600">{amenity.description}</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            amenity.available
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                                {amenity.available ? 'Available' : 'Not Available'}
                                                            </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 text-gray-700">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={amenity.notes || ''}
                                                            onChange={(e) => {
                                                                setCampDetails(prev => ({
                                                                    ...prev,
                                                                    amenities: prev.amenities.map(a =>
                                                                        a.id === amenity.id
                                                                            ? { ...a, notes: e.target.value }
                                                                            : a
                                                                    )
                                                                }));
                                                            }}
                                                            onBlur={(e) => {
                                                                handleAmenityUpdate(amenity.id, 'notes', e.target.value);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            placeholder="Add notes about this amenity..."
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}




                            {campDetails?.amenities?.filter(a => a.category === 'activities').length > 0 && (
                                <div>
                                    <h4 className="font-medium text-cyan-800 mb-3 bg-cyan-100 px-3 py-2 rounded-md">Activities</h4>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {campDetails.amenities
                                            .filter(amenity => amenity.category === 'activities')
                                            .map(amenity => (
                                                <div key={amenity.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={amenity.available}
                                                                    onChange={(e) => handleAmenityUpdate(amenity.id, 'available', e.target.checked)}
                                                                    className="h-4 w-4 text-cyan-600 mr-3"
                                                                />
                                                                <div>
                                                                        <span className={`font-medium ${amenity.available ? 'text-green-700' : 'text-gray-500'}`}>
                                                                            {amenity.name}
                                                                        </span>
                                                                    <p className="text-xs text-gray-600">{amenity.description}</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            amenity.available
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                                {amenity.available ? 'Available' : 'Not Available'}
                                                            </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 text-gray-700">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={amenity.notes || ''}
                                                            onChange={(e) => {
                                                                setCampDetails(prev => ({
                                                                    ...prev,
                                                                    amenities: prev.amenities.map(a =>
                                                                        a.id === amenity.id
                                                                            ? { ...a, notes: e.target.value }
                                                                            : a
                                                                    )
                                                                }));
                                                            }}
                                                            onBlur={(e) => {
                                                                handleAmenityUpdate(amenity.id, 'notes', e.target.value);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            placeholder="Add notes about this amenity..."
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}



                            {campDetails?.amenities?.filter(a => a.category === 'services').length > 0 && (
                                <div>
                                    <h4 className="font-medium text-cyan-800 mb-3 bg-cyan-100 px-3 py-2 rounded-md">Services</h4>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {campDetails.amenities
                                            .filter(amenity => amenity.category === 'services')
                                            .map(amenity => (
                                                <div key={amenity.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={amenity.available}
                                                                    onChange={(e) => handleAmenityUpdate(amenity.id, 'available', e.target.checked)}
                                                                    className="h-4 w-4 text-cyan-600 mr-3"
                                                                />
                                                                <div>
                                                                        <span className={`font-medium ${amenity.available ? 'text-green-700' : 'text-gray-500'}`}>
                                                                            {amenity.name}
                                                                        </span>
                                                                    <p className="text-xs text-gray-600">{amenity.description}</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            amenity.available
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                                {amenity.available ? 'Available' : 'Not Available'}
                                                            </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 text-gray-700">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={amenity.notes || ''}
                                                            onChange={(e) => {
                                                                setCampDetails(prev => ({
                                                                    ...prev,
                                                                    amenities: prev.amenities.map(a =>
                                                                        a.id === amenity.id
                                                                            ? { ...a, notes: e.target.value }
                                                                            : a
                                                                    )
                                                                }));
                                                            }}
                                                            onBlur={(e) => {
                                                                handleAmenityUpdate(amenity.id, 'notes', e.target.value);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            placeholder="Add notes about this amenity..."
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}



                            {campDetails?.amenities?.filter(a => a.category === 'access').length > 0 && (
                                <div>
                                    <h4 className="font-medium text-cyan-800 mb-3 bg-cyan-100 px-3 py-2 rounded-md">Access</h4>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {campDetails.amenities
                                            .filter(amenity => amenity.category === 'access')
                                            .map(amenity => (
                                                <div key={amenity.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={amenity.available}
                                                                    onChange={(e) => handleAmenityUpdate(amenity.id, 'available', e.target.checked)}
                                                                    className="h-4 w-4 text-cyan-600 mr-3"
                                                                />
                                                                <div>
                                                                        <span className={`font-medium ${amenity.available ? 'text-green-700' : 'text-gray-500'}`}>
                                                                            {amenity.name}
                                                                        </span>
                                                                    <p className="text-xs text-gray-600">{amenity.description}</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            amenity.available
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                                {amenity.available ? 'Available' : 'Not Available'}
                                                            </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 text-gray-700">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={amenity.notes || ''}
                                                            onChange={(e) => {
                                                                setCampDetails(prev => ({
                                                                    ...prev,
                                                                    amenities: prev.amenities.map(a =>
                                                                        a.id === amenity.id
                                                                            ? { ...a, notes: e.target.value }
                                                                            : a
                                                                    )
                                                                }));
                                                            }}
                                                            onBlur={(e) => {
                                                                handleAmenityUpdate(amenity.id, 'notes', e.target.value);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            placeholder="Add notes about this amenity..."
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}




                            {/* No Amenities Message */}
                            {(!campDetails?.amenities || campDetails.amenities.length === 0) && (
                                <div className="text-center py-8 text-gray-500">
                                    <Wrench className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                    <p>No amenities configured for this campground yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>

                {error && (
                    <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <span className="ml-2 text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                <div className="p-6 space-y-6">
                    {/* Location Information - Collapsible */}
                    <div className="bg-green-50 rounded-lg overflow-hidden border border-green-100">
                        <button
                            onClick={handleLocationToggle}
                            className="w-full px-4 py-4 flex items-center justify-between hover:bg-green-100 transition-colors bg-green-50"
                        >
                            <div className="flex items-center">
                                <MapPin className="h-5 w-5 mr-2 text-green-600" />
                                <h3 className="text-lg font-semibold text-green-800">Location Information</h3>
                                {/* Google Maps Icon */}
                                {campDetails?.basic_info?.latitude && campDetails?.basic_info?.longitude && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openGoogleMaps();
                                        }}
                                        className="ml-3 p-1 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-md transition-colors"
                                        title="View on Google Maps"
                                    >
                                        <GoogleMapsIcon className="h-6 w-6" />
                                    </button>
                                )}
                            </div>
                            {locationExpanded ? (
                                <ChevronUp className="h-5 w-5 text-green-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-green-500" />
                            )}
                        </button>

                        {locationExpanded && (
                            <div className="px-4 pb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Elevation (feet)</label>
                                        <input
                                            type="number"
                                            value={formData.elevation_feet || ''}
                                            onChange={(e) => handleInputChange('elevation_feet', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                            placeholder="Enter elevation in feet"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Address</label>
                                        <textarea
                                            value={formData.address || ''}
                                            onChange={(e) => handleInputChange('address', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                            rows="2"
                                            placeholder="Enter address"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">City</label>
                                        <input
                                            type="text"
                                            value={formData.city || ''}
                                            onChange={(e) => handleInputChange('city', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                            placeholder="Enter city"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">State</label>
                                        <input
                                            type="text"
                                            value={formData.state || 'California'}
                                            onChange={(e) => handleInputChange('state', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                            placeholder="Enter state"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">ZIP Code</label>
                                        <input
                                            type="text"
                                            value={formData.zip_code || ''}
                                            onChange={(e) => handleInputChange('zip_code', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                            placeholder="Enter ZIP code"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Site Details - Collapsible */}
                    <div className="bg-purple-50 rounded-lg overflow-hidden border border-purple-100">
                        <button
                            onClick={handleSiteDetailsToggle}
                            className="w-full px-4 py-4 flex items-center justify-between hover:bg-purple-100 transition-colors bg-purple-50"
                        >
                            <div className="flex items-center">
                                <Tent className="h-5 w-5 mr-2 text-purple-600" />
                                <h3 className="text-lg font-semibold text-purple-800">Site Details</h3>
                            </div>
                            {siteDetailsExpanded ? (
                                <ChevronUp className="h-5 w-5 text-purple-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-purple-500" />
                            )}
                        </button>

                        {siteDetailsExpanded && (
                            <div className="px-4 pb-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Total Sites</label>
                                        <input
                                            type="number"
                                            value={formData.total_sites || ''}
                                            onChange={(e) => handleInputChange('total_sites', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Tent Sites</label>
                                        <input
                                            type="number"
                                            value={formData.tent_sites || ''}
                                            onChange={(e) => handleInputChange('tent_sites', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">RV Sites</label>
                                        <input
                                            type="number"
                                            value={formData.rv_sites || ''}
                                            onChange={(e) => handleInputChange('rv_sites', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Group Sites</label>
                                        <input
                                            type="number"
                                            value={formData.group_sites || ''}
                                            onChange={(e) => handleInputChange('group_sites', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Max RV Length (feet)</label>
                                        <input
                                            type="number"
                                            value={formData.max_rv_length || ''}
                                            onChange={(e) => handleInputChange('max_rv_length', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Surface Type</label>
                                        <select
                                            value={formData.surface_type || ''}
                                            onChange={(e) => handleInputChange('surface_type', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        >
                                            <option value="">Select surface type</option>
                                            <option value="dirt">Dirt</option>
                                            <option value="gravel">Gravel</option>
                                            <option value="paved">Paved</option>
                                            <option value="grass">Grass</option>
                                            <option value="sand">Sand</option>
                                            <option value="mixed">Mixed</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Privacy Level</label>
                                        <select
                                            value={formData.privacy_level || 'unknown'}
                                            onChange={(e) => handleInputChange('privacy_level', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        >
                                            <option value="very_private">Very Private</option>
                                            <option value="private">Private</option>
                                            <option value="semi_private">Semi Private</option>
                                            <option value="open">Open</option>
                                            <option value="unknown">Unknown</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Shade Available</label>
                                        <select
                                            value={formData.shade_available || 'unknown'}
                                            onChange={(e) => handleInputChange('shade_available', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        >
                                            <option value="full_shade">Full Shade</option>
                                            <option value="partial_shade">Partial Shade</option>
                                            <option value="no_shade">No Shade</option>
                                            <option value="mixed">Mixed</option>
                                            <option value="unknown">Unknown</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Terrain</label>
                                        <select
                                            value={formData.terrain || 'unknown'}
                                            onChange={(e) => handleInputChange('terrain', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        >
                                            <option value="level">Level</option>
                                            <option value="semi-level">Semi-Level</option>
                                            <option value="on a slope">On a Slope</option>
                                            <option value="unknown">Unknown</option>
                                        </select>
                                    </div>
                                </div>


                            </div>
                        )}
                    </div>

                    {/* Access Requirements - Collapsible */}
                    <div className="bg-amber-50 rounded-lg overflow-hidden border border-amber-100">
                        <button
                            onClick={handleAccessRequirementsToggle}
                            className="w-full px-4 py-4 flex items-center justify-between hover:bg-amber-100 transition-colors bg-amber-50"
                        >
                            <div className="flex items-center">
                                <Shield className="h-5 w-5 mr-2 text-amber-600"/>
                                <h3 className="text-lg font-semibold text-amber-800">Access Requirements</h3>
                            </div>
                            {accessRequirementsExpanded ? (
                                <ChevronUp className="h-5 w-5 text-amber-500"/>
                            ) : (
                                <ChevronDown className="h-5 w-5 text-amber-500"/>
                            )}
                        </button>

                        {accessRequirementsExpanded && (
                            <div className="px-4 pb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="four_wheel_drive_required"
                                            checked={formData.four_wheel_drive_required || false}
                                            onChange={(e) => handleInputChange('four_wheel_drive_required', e.target.checked)}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <label htmlFor="four_wheel_drive_required" className="ml-2 text-sm font-medium">
                                            Four Wheel Drive Required
                                        </label>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="high_clearance_required"
                                            checked={formData.high_clearance_required || false}
                                            onChange={(e) => handleInputChange('high_clearance_required', e.target.checked)}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <label htmlFor="high_clearance_required" className="ml-2 text-sm font-medium">
                                            High Clearance Required
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Road Conditions</label>
                                        <select
                                            value={formData.road_conditions || 'unknown'}
                                            onChange={(e) => handleInputChange('road_conditions', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        >
                                            <option value="paved">Paved</option>
                                            <option value="gravel">Gravel</option>
                                            <option value="dirt">Dirt</option>
                                            <option value="rough">Rough</option>
                                            <option value="seasonal">Seasonal</option>
                                            <option value="unknown">Unknown</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Contact & Booking - Collapsible */}
                    {!campDetails?.isDispersed && (
                        <div className="bg-pink-50 rounded-lg overflow-hidden border border-pink-100">
                            <button
                                onClick={handleContactBookingToggle}
                                className="w-full px-4 py-4 flex items-center justify-between hover:bg-pink-100 transition-colors bg-pink-50"
                            >
                                <div className="flex items-center">
                                    <Phone className="h-5 w-5 mr-2 text-pink-600" />
                                    <h3 className="text-lg font-semibold text-pink-800">Contact & Booking</h3>
                                </div>
                                {contactBookingExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-pink-500" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-pink-500" />
                                )}
                            </button>

                            {contactBookingExpanded && (
                                <div className="px-4 pb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Website URL</label>
                                            <input
                                                type="url"
                                                value={formData.website_url || ''}
                                                onChange={(e) => handleInputChange('website_url', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="https://example.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Booking URL</label>
                                            <input
                                                type="url"
                                                value={formData.booking_url || ''}
                                                onChange={(e) => handleInputChange('booking_url', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="https://booking.example.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Phone</label>
                                            <input
                                                type="tel"
                                                value={formData.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="(555) 123-4567"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Email</label>
                                            <input
                                                type="email"
                                                value={formData.email || ''}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="contact@example.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="reservation_required"
                                                checked={formData.reservation_required || false}
                                                onChange={(e) => handleInputChange('reservation_required', e.target.checked)}
                                                className="h-4 w-4 text-blue-600"
                                            />
                                            <label htmlFor="reservation_required" className="ml-2 text-sm font-medium">
                                                Reservation Required
                                            </label>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Advance Booking Days</label>
                                            <input
                                                type="number"
                                                value={formData.advance_booking_days || ''}
                                                onChange={(e) => handleInputChange('advance_booking_days', e.target.value ? parseInt(e.target.value) : null)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="Number of days"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Operational Info - Collapsible */}
                    <div className="bg-teal-50 rounded-lg overflow-hidden border border-teal-100">
                        <button
                            onClick={handleOperationalToggle}
                            className="w-full px-4 py-4 flex items-center justify-between hover:bg-teal-100 transition-colors bg-teal-50"
                        >
                            <div className="flex items-center">
                                <Clock className="h-5 w-5 mr-2 text-teal-600" />
                                <h3 className="text-lg font-semibold text-teal-800">Operational Information</h3>
                            </div>
                            {operationalExpanded ? (
                                <ChevronUp className="h-5 w-5 text-teal-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-teal-500" />
                            )}
                        </button>

                        {operationalExpanded && (
                            <div className="px-4 pb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Season Start</label>
                                        <input
                                            type="date"
                                            value={formData.open_season_start || ''}
                                            onChange={(e) => handleInputChange('open_season_start', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Season End</label>
                                        <input
                                            type="date"
                                            value={formData.open_season_end || ''}
                                            onChange={(e) => handleInputChange('open_season_end', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="year_round"
                                            checked={formData.year_round || false}
                                            onChange={(e) => handleInputChange('year_round', e.target.checked)}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <label htmlFor="year_round" className="ml-2 text-sm font-medium">
                                            Open Year Round
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Max Stay (days)</label>
                                        <input
                                            type="number"
                                            value={formData.max_stay_days || ''}
                                            onChange={(e) => handleInputChange('max_stay_days', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Check-in Time</label>
                                        <input
                                            type="time"
                                            value={formData.check_in_time || ''}
                                            onChange={(e) => handleInputChange('check_in_time', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Check-out Time</label>
                                        <input
                                            type="time"
                                            value={formData.check_out_time || ''}
                                            onChange={(e) => handleInputChange('check_out_time', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Fees - Collapsible */}
                    {!campDetails?.isDispersed && (
                        <div className="bg-orange-50 rounded-lg overflow-hidden border border-orange-100">
                            <button
                                onClick={handleFeesToggle}
                                className="w-full px-4 py-4 flex items-center justify-between hover:bg-orange-100 transition-colors bg-orange-50"
                            >
                                <div className="flex items-center">
                                    <DollarSign className="h-5 w-5 mr-2 text-orange-600" />
                                    <h3 className="text-lg font-semibold text-orange-800">Fees</h3>
                                </div>
                                {feesExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-orange-500" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-orange-500" />
                                )}
                            </button>

                            {feesExpanded && (
                                <div className="px-4 pb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Fee Per Night</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.fee_per_night || ''}
                                                onChange={(e) => handleInputChange('fee_per_night', e.target.value ? parseFloat(e.target.value) : null)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Currency</label>
                                            <input
                                                type="text"
                                                value={formData.fee_currency || 'USD'}
                                                onChange={(e) => handleInputChange('fee_currency', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                maxLength="3"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Day Use Fee</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.day_use_fee || ''}
                                                onChange={(e) => handleInputChange('day_use_fee', e.target.value ? parseFloat(e.target.value) : null)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Entrance Fee</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.entrance_fee || ''}
                                                onChange={(e) => handleInputChange('entrance_fee', e.target.value ? parseFloat(e.target.value) : null)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="free_camping"
                                                checked={formData.free_camping || false}
                                                onChange={(e) => handleInputChange('free_camping', e.target.checked)}
                                                className="h-4 w-4 text-blue-600"
                                            />
                                            <label htmlFor="free_camping" className="ml-2 text-sm font-medium">
                                                Free Camping
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Administrative - Collapsible */}
                    <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-100">
                            <button
                                onClick={handleAdministrativeToggle}
                                className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-100 transition-colors bg-slate-50"
                            >
                                <div className="flex items-center">
                                    <Building className="h-5 w-5 mr-2 text-slate-600" />
                                    <h3 className="text-lg font-semibold text-slate-800">Administrative</h3>
                                </div>
                                {administrativeExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-slate-500" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-slate-500" />
                                )}
                            </button>

                            {administrativeExpanded && (
                                <div className="px-4 pb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Managing Agency</label>
                                            <input
                                                type="text"
                                                value={formData.managing_agency || ''}
                                                onChange={(e) => handleInputChange('managing_agency', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="e.g., National Park Service"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Last Verified Date</label>
                                            <input
                                                type="date"
                                                value={formData.last_verified_date || ''}
                                                onChange={(e) => handleInputChange('last_verified_date', e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>


                    {/* Submit Button Section - Enhanced with "Save & Return to Map" option */}
                    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                        {/* Back Button */}
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 hover:text-gray-700 transition-colors"
                            title="Go Back"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </button>
                        
                        {/* Right side buttons */}
                        <div className="flex space-x-3">
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                            >
                            {saving ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin"/>
                            ) : (
                                <Save className="h-4 w-4 mr-1"/>
                            )}
                            {saving ? 'Saving...' : 'Save'}
                        </button>

                            {/* Save & Return to Map button - only show if we have map state */}
                            {getReturnMapState() && (
                                <button
                                    onClick={saveAndReturnToMap}
                                    disabled={saving}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                                >
                                    {saving ? (
                                        <Loader2 className="h-4 w-4 mr-1 animate-spin"/>
                                    ) : (
                                        <Map className="h-4 w-4 mr-1"/>
                                    )}
                                    {saving ? 'Saving...' : 'Save & Return'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Photo Upload Section - Collapsible */}
                    <div className="bg-indigo-50 rounded-lg overflow-hidden border border-indigo-100">
                        <button
                            onClick={handlePhotoUploaderToggle}
                            className="w-full px-4 py-4 flex items-center justify-between hover:bg-indigo-100 transition-colors bg-indigo-50"
                        >
                            <div className="flex items-center">
                                <Camera className="h-5 w-5 mr-2 text-indigo-600" />
                                <h3 className="text-lg font-semibold text-indigo-800">Upload Camp Photos</h3>
                            </div>
                            {photoUploaderExpanded ? (
                                <ChevronUp className="h-5 w-5 text-indigo-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-indigo-500" />
                            )}
                        </button>

                        {photoUploaderExpanded && (
                            <div className="px-4 pb-4">
                                <PhotoUpload
                                    campLocationId={camp_location_id}
                                    allowFeaturedSelection={true}
                                    defaultPhotoType="general"
                                    maxFiles={10}
                                    onUploadSuccess={(uploadData) => {
                                        console.log('Photo uploaded successfully:', uploadData);
                                        toast.success(`Photo "${uploadData.filename}" uploaded successfully!`);
                                        // Refresh the photo gallery
                                        setGalleryRefreshKey(prev => prev + 1);
                                    }}
                                    onUploadError={(error, filename) => {
                                        console.error('Photo upload failed:', error, filename);
                                        toast.error(`Failed to upload ${filename}: ${error}`);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    </div>
    );
};

export default CampDetailsEditor;